import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { ApiError } from '../lib/api';
import { verifyUnsubscribeToken, confirmUnsubscribe, categoryLabel } from '../lib/api/emailPreferences';

// Public, no-auth unsubscribe confirmation page reached from the visible
// "Unsubscribe" link in a marketing email (the RFC 8058 one-click header is a
// separate, headless path). The token in ?t= is the only credential: verify it,
// show what's being turned off, confirm on the user's click.

type Phase = 'verifying' | 'ready' | 'submitting' | 'done' | 'error';

export default function EmailPreferencesPage() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const [phase, setPhase] = useState<Phase>('verifying');
  const [category, setCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applyPageMeta(PAGE_META.emailPreferences);
  }, []);

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setError('This unsubscribe link is missing its token. Please use the link from your email.');
      return;
    }
    const controller = new AbortController();
    verifyUnsubscribeToken(token, { signal: controller.signal })
      .then((d) => {
        setCategory(d.category);
        setPhase('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setPhase('error');
        setError(
          err instanceof ApiError && err.status === 400
            ? 'This unsubscribe link is invalid or has expired.'
            : 'We couldn’t verify this unsubscribe link. Please try again.',
        );
      });
    return () => controller.abort();
  }, [token]);

  const onConfirm = async (): Promise<void> => {
    setPhase('submitting');
    setError(null);
    try {
      await confirmUnsubscribe(token);
      setPhase('done');
    } catch {
      setPhase('error');
      setError('We couldn’t process your unsubscribe. Please try again.');
    }
  };

  const label = category ? categoryLabel(category) : 'these emails';

  return (
    <article className="mx-auto max-w-xl px-6 py-16 text-white/85">
      <header className="mb-6">
        <h1 className="font-display text-2xl uppercase tracking-widest text-brand-redAccent">Email preferences</h1>
      </header>

      {phase === 'verifying' && <p className="text-white/60">Verifying your link…</p>}

      {phase === 'ready' && (
        <div className="flex flex-col gap-5">
          <p>
            Do you want to unsubscribe from <span className="font-medium text-white">{label}</span>? You’ll stop
            receiving these emails, but your account and gameplay are unaffected.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void onConfirm()}
              className="rounded-full bg-white/90 px-6 py-2.5 text-sm font-medium text-[#2a2237] transition hover:bg-white"
            >
              Unsubscribe
            </button>
            <Link
              to="/"
              className="rounded-full border border-white/25 px-6 py-2.5 text-sm text-white transition hover:bg-white/10"
            >
              Keep my subscription
            </Link>
          </div>
        </div>
      )}

      {phase === 'submitting' && <p className="text-white/60">Updating your preferences…</p>}

      {phase === 'done' && (
        <div className="flex flex-col gap-4">
          <p>
            You’ve been unsubscribed from <span className="font-medium text-white">{label}</span>. We’re sorry to
            see you go.
          </p>
          <p className="text-sm text-white/60">
            Changed your mind? You can re-enable reminders anytime from your{' '}
            <Link to="/account" className="text-brand-redAccent underline">
              account settings
            </Link>
            .
          </p>
        </div>
      )}

      {phase === 'error' && (
        <div className="flex flex-col gap-4">
          <p className="text-[#ff8da6]">{error}</p>
          <p className="text-sm text-white/60">
            You can manage all your email preferences from your{' '}
            <Link to="/account" className="text-brand-redAccent underline">
              account settings
            </Link>
            .
          </p>
        </div>
      )}
    </article>
  );
}
